<template>
    <div>
        <el-dialog
            align-center
            v-model="state.dialogVisible"
            :title="t('attendance.manualOtRecordTitle')"
            width="920"
            draggable
            @closed="resetForm"
        >
            <div class="manual-record-form">
                <el-form ref="ruleForm" label-width="auto" :model="state.form" :rules="rules">
                    <el-form-item :label="t('attendance.captainName')" prop="captain_id">
                        <el-select
                            v-model="state.form.captain_id"
                            filterable
                            clearable
                            :placeholder="t('attendance.manualRecordSelectCaptain')"
                            style="width: 100%"
                            @change="onCaptainChange"
                        >
                            <el-option
                                v-for="item in state.captainOptions"
                                :key="item.id"
                                :label="`${item.real_name || item.realName || '-'}${item.mobile ? '（' + item.mobile + '）' : ''}`"
                                :value="item.id"
                            />
                        </el-select>
                    </el-form-item>
                    <el-form-item :label="t('attendance.manualRecordTeamList')" prop="selected_user_ids">
                        <div class="machinist-table-wrap">
                            <div v-if="state.loadingMachinists" class="table-loading">
                                {{ t('common.loading') }}
                            </div>
                            <el-empty
                                v-else-if="!state.form.captain_id"
                                :description="t('attendance.manualRecordSelectCaptainFirst')"
                                :image-size="56"
                            />
                            <el-empty
                                v-else-if="state.machinistRows.length === 0"
                                :description="t('attendance.manualRecordNoTeam')"
                                :image-size="56"
                            />
                            <el-table
                                v-else
                                ref="machinistTable"
                                :data="state.machinistRows"
                                max-height="280"
                                border
                                size="small"
                                @selection-change="onSelectionChange"
                            >
                                <el-table-column type="selection" width="48" :selectable="rowSelectable" />
                                <el-table-column :label="t('field.realName')" min-width="120">
                                    <template #default="{ row }">
                                        <span>{{ row.real_name || '-' }}</span>
                                        <el-tag v-if="row.is_captain" size="small" type="warning" class="captain-tag">
                                            {{ t('attendance.captain') }}
                                        </el-tag>
                                    </template>
                                </el-table-column>
                                <el-table-column :label="t('field.mobile')" min-width="110" prop="mobile" />
                                <el-table-column :label="t('detail.deviceLabel')" min-width="220">
                                    <template #default="{ row }">
                                        <span v-if="row.noDevice" class="no-device">{{ t('attendance.manualRecordNoDevice') }}</span>
                                        <el-select
                                            v-else
                                            v-model="row.device_list_id"
                                            filterable
                                            size="small"
                                            style="width: 100%"
                                            :placeholder="t('attendance.manualRecordSelectDevice')"
                                        >
                                            <el-option
                                                v-for="item in row.device_options"
                                                :key="item.device_list_id"
                                                :label="deviceOptionLabel(item)"
                                                :value="item.device_list_id"
                                            />
                                        </el-select>
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                    </el-form-item>
                    <el-form-item :label="t('attendance.makeupDate')" prop="checkin_date">
                        <el-date-picker
                            v-model="state.form.checkin_date"
                            type="date"
                            value-format="YYYY-MM-DD"
                            :placeholder="t('common.pleaseSelect')"
                            style="width: 100%"
                        />
                    </el-form-item>
                    <el-form-item :label="t('attendance.manualOtRecordCheckinTime')" prop="checkin_time">
                        <el-time-picker
                            v-model="state.form.checkin_time"
                            format="HH:mm"
                            value-format="HH:mm"
                            :placeholder="t('attendance.manualOtRecordDefaultTime')"
                            style="width: 100%"
                        />
                    </el-form-item>
                    <el-form-item :label="t('attendance.manualOtRecordTypes')">
                        <el-checkbox v-model="state.form.record_ot">{{ t('attendance.ot') }}</el-checkbox>
                        <el-checkbox v-model="state.form.record_zhongzhi">{{ t('attendance.zhongzhi') }}</el-checkbox>
                    </el-form-item>
                    <el-form-item v-if="state.form.record_ot" :label="t('attendance.manualOtRecordOtHours')" prop="ot_hours">
                        <el-input-number
                            v-model="state.form.ot_hours"
                            :min="1"
                            :max="14"
                            :step="1"
                            controls-position="right"
                            style="width: 160px"
                        />
                        <span class="unit-text">{{ t('detail.unitHour') }}</span>
                    </el-form-item>
                    <el-form-item :label="t('attendance.workContent')" prop="contents">
                        <el-input
                            v-model="state.form.contents"
                            :placeholder="t('attendance.manualOtRecordContentsPlaceholder')"
                        />
                    </el-form-item>
                    <el-form-item :label="t('attendance.location')" prop="address">
                        <el-input
                            v-model="state.form.address"
                            :placeholder="t('attendance.manualRecordAddressPlaceholder')"
                        />
                    </el-form-item>
                    <el-alert
                        :title="t('attendance.manualOtRecordHint')"
                        type="info"
                        :closable="false"
                        show-icon
                    />
                </el-form>
            </div>
            <template #footer>
                <el-button @click="close">{{ t('common.cancel') }}</el-button>
                <el-button type="primary" :loading="state.submitting" @click="submit">
                    {{ t('common.confirm') }}
                </el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { reactive, ref, defineEmits, defineExpose, computed, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { t } from '@/locale/index.js'
import { pageListApi as userListApi } from '@/api/system/personnel'
import { getUserDeviceListApi } from '@/api/machinist/detail'
import { adminManualSaveApi } from '@/api/attendance/OTDR'

const emit = defineEmits(['refresh'])

const todayStr = () => {
    const d = new Date()
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const defaultForm = () => ({
    captain_id: null,
    selected_user_ids: [],
    checkin_date: todayStr(),
    checkin_time: '18:00',
    record_ot: true,
    record_zhongzhi: true,
    ot_hours: 1,
    contents: '',
    address: '',
})

const state = reactive({
    dialogVisible: false,
    submitting: false,
    loadingMachinists: false,
    captainOptions: [],
    machinistRows: [],
    selectedRows: [],
    form: defaultForm(),
})

const rules = computed(() => ({
    captain_id: [{ required: true, message: t('attendance.manualRecordSelectCaptain'), trigger: 'change' }],
    checkin_date: [{ required: true, message: t('attendance.makeupDate'), trigger: 'change' }],
    selected_user_ids: [{
        validator: (_rule, _value, callback) => {
            if (!state.form.selected_user_ids.length) {
                callback(new Error(t('attendance.manualRecordSelectMachinist')))
                return
            }
            callback()
        },
        trigger: 'change',
    }],
}))

const ruleForm = ref(null)
const machinistTable = ref(null)

const deviceOptionLabel = (item) => {
    const title = item.title || '-'
    const code = item.code || '-'
    return `${title}（${code}）`
}

const rowSelectable = (row) => !row.noDevice

const buildUserRow = async (user, isCaptain = false) => {
    const devRes = await getUserDeviceListApi(JSON.stringify({
        user_id: user.id,
        page: 1,
        limit: 9999,
    }))
    const deviceOptions = (devRes?.data ?? []).map((item) => ({
        device_list_id: item.device_list_id || item.id,
        title: item.title || '',
        code: item.code || '',
    }))
    const defaultDeviceId = deviceOptions[0]?.device_list_id || null
    return {
        user_id: user.id,
        real_name: user.real_name || user.realName,
        mobile: user.mobile || '',
        is_captain: isCaptain,
        device_options: deviceOptions,
        device_list_id: defaultDeviceId,
        noDevice: deviceOptions.length === 0,
    }
}

const loadCaptains = () => {
    userListApi(JSON.stringify({
        page: 1,
        limit: 9999,
        keywords: '',
        role_id: 2,
    })).then((res) => {
        state.captainOptions = res?.data ?? []
    })
}

const loadCaptainMachinists = async (captainId) => {
    state.machinistRows = []
    state.selectedRows = []
    state.form.selected_user_ids = []
    if (!captainId) {
        return
    }
    state.loadingMachinists = true
    try {
        const captain = state.captainOptions.find((item) => item.id === captainId)
        const res = await userListApi(JSON.stringify({
            page: 1,
            limit: 9999,
            keywords: '',
            role_id: 1,
            replied_by: captainId,
        }))
        const teamUsers = []
        if (captain) {
            teamUsers.push({ user: captain, isCaptain: true })
        }
        ;(res?.data ?? []).forEach((item) => {
            if (item.id !== captainId) {
                teamUsers.push({ user: item, isCaptain: false })
            }
        })
        const rows = await Promise.all(teamUsers.map(({ user, isCaptain }) => buildUserRow(user, isCaptain)))
        state.machinistRows = rows
        await nextTick()
        rows.forEach((row) => {
            if (!row.noDevice) {
                machinistTable.value?.toggleRowSelection?.(row, true)
            }
        })
        state.selectedRows = rows.filter((row) => !row.noDevice)
        state.form.selected_user_ids = state.selectedRows.map((row) => row.user_id)
        ruleForm.value?.validateField?.('selected_user_ids')
    } finally {
        state.loadingMachinists = false
    }
}

const onCaptainChange = (captainId) => {
    loadCaptainMachinists(captainId)
}

const onSelectionChange = (selection) => {
    state.selectedRows = selection
    state.form.selected_user_ids = selection.map((row) => row.user_id)
    ruleForm.value?.validateField?.('selected_user_ids')
}

const open = () => {
    state.form = defaultForm()
    state.machinistRows = []
    state.selectedRows = []
    loadCaptains()
    state.dialogVisible = true
}

const close = () => {
    state.dialogVisible = false
}

const resetForm = () => {
    state.form = defaultForm()
    state.machinistRows = []
    state.selectedRows = []
    machinistTable.value?.clearSelection?.()
    ruleForm.value?.clearValidate?.()
}

const submit = () => {
    ruleForm.value?.validate((valid) => {
        if (!valid) return
        if (!state.form.record_ot && !state.form.record_zhongzhi) {
            ElMessage.warning(t('attendance.manualOtRecordTypeRequired'))
            return
        }
        if (state.selectedRows.some((row) => !row.device_list_id)) {
            ElMessage.warning(t('attendance.manualRecordDeviceRequired'))
            return
        }
        state.submitting = true
        adminManualSaveApi(JSON.stringify({
            captain_id: state.form.captain_id,
            entries: state.selectedRows.map((row) => ({
                user_id: row.user_id,
                device_list_id: row.device_list_id,
            })),
            checkin_date: state.form.checkin_date,
            checkin_time: state.form.checkin_time || '18:00',
            record_ot: state.form.record_ot,
            record_zhongzhi: state.form.record_zhongzhi,
            ot_hours: state.form.ot_hours || 1,
            contents: state.form.contents || undefined,
            address: state.form.address || undefined,
        })).then((res) => {
            if (res?.code === 200) {
                ElMessage.success(res.msg || t('common.success'))
                close()
                emit('refresh')
            } else {
                ElMessage.error(res?.msg || t('attendance.saveFailed'))
            }
        }).finally(() => {
            state.submitting = false
        })
    })
}

defineExpose({ open })
</script>

<style lang="less" scoped>
.manual-record-form {
    padding: 0 8px;
}

.machinist-table-wrap {
    width: 100%;
}

.table-loading {
    padding: 24px 0;
    text-align: center;
    color: #909399;
}

.no-device {
    color: #f56c6c;
}

.captain-tag {
    margin-left: 6px;
}

.unit-text {
    margin-left: 8px;
    color: #606266;
}
</style>
